import React from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import GetAppIcon from '@mui/icons-material/GetApp';
import { dateFormat } from '../../utils/dateFunctions';
import { getAPIData, postApi } from '../../utils/apiFunction';
import OTRLabels from './CreateOTRLabelsfromGroup';
import createOTRPDF from './CreateOTRPDF';
import { delIcon } from '../../themes/myStyles';
import { numberFormat } from '../../utils/numberFunctions';
import useSwalWrapper from '@jumbo/vendors/sweetalert2/hooks';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const formatWarehouse = (w) => {
    if (!w) return '';
    if (w.isFarm) return w.name || w.description || 'Farm';
    return w.description || w.name || '';
};

const formatLease = (lease) => {
    if (!lease) return '';
    const name = lease.displayName || lease.name || lease.leaseNo || lease.number || '';
    const number = lease.number && (!name || name === lease.name) ? lease.number : null;
    if (name && number && !name.includes(number)) {
        return `${name} (${number})`;
    }
    return name || number || '';
};

const formatEstuary = (lease) => {
    return lease?.harvestArea?.estuary?.description || lease?.estuary?.description || lease?.estuary || '';
};

const formatHarvestArea = (lease) => {
    return lease?.harvestArea?.description || lease?.harvestArea || '';
};

const InfoRow = ({ label, value }) => (
    <Grid container spacing={1} sx={{ py: 0.5 }}>
        <Grid item xs={4} md={3}>
            <Typography variant="body2" color="text.secondary">{label}</Typography>
        </Grid>
        <Grid item xs={8} md={9}>
            <Typography variant="body1">{value || 'N/A'}</Typography>
        </Grid>
    </Grid>
);

const LineRow = ({ line }) => {
    const desc = React.useMemo(() => {
        if (line?.__mixedGroupBreakdown?.length) {
            return line.__mixedGroupBreakdown.map(p => `${p.grade} ${numberFormat(p.qty)}`).join(' / ');
        }
        const species = line?.stockItem?.species?.description;
        const grade = line?.stockItem?.grade?.description;
        if (species && grade) return `${species} ${grade}`;
        return line?.stockItem?.description || 'Unknown';
    }, [line]);

    return (
        <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="subtitle2">{desc}</Typography>
            <Grid container spacing={1} sx={{ mt: 0.5 }}>
                <Grid item xs={4}><Typography variant="body2" color="text.secondary">Bags</Typography><Typography variant="body1">{numberFormat(line?.bags || 0)}</Typography></Grid>
                <Grid item xs={4}><Typography variant="body2" color="text.secondary">Bins</Typography><Typography variant="body1">{numberFormat(line?.bins || 0)}</Typography></Grid>
                <Grid item xs={4}><Typography variant="body2" color="text.secondary">Quantity</Typography><Typography variant="body1">{numberFormat(line?.quantity || 0)}</Typography></Grid>
            </Grid>
            <Grid container spacing={1} sx={{ mt: 0.5 }}>
                {line?.lease?.displayName ? (
                    <Grid item xs={12} sm={6}><Typography variant="body2" color="text.secondary">Lease</Typography><Typography variant="body1">{line.lease.displayName}</Typography></Grid>
                ) : null}
                {line?.crop ? (
                    <Grid item xs={12} sm={6}><Typography variant="body2" color="text.secondary">Crop Year</Typography><Typography variant="body1">{line.crop}</Typography></Grid>
                ) : null}
            </Grid>
        </Box>
    );
};

const ViewTransfer = (props) => {
    const id = props?.data?.id || props?.transferId;
    const [transfer, setTransfer] = React.useState(props?.data || null);
    const [loadingPdf, setLoadingPdf] = React.useState(false);
    const [customers, setCustomers] = React.useState([]);
    const [loadingCancel, setLoadingCancel] = React.useState(false);

    const uri = process.env.REACT_APP_BASE_URL;
    const Swal = useSwalWrapper();

    React.useEffect(() => {
        if (props?.data) {
            setTransfer(props.data);
        }
    }, [props?.data]);

    React.useEffect(() => {
        if (id && (!props?.data || !props?.data?.coreTransferLines)) {
            getAPIData('/Transfers/Get/' + id, setTransfer);
        }
    }, [id, props?.data]);

    React.useEffect(() => {
        getAPIData('/Transfers/Customers', setCustomers);
    }, []);

    const getCustomerName = React.useCallback((customerId) => {
        if (!customerId || !customers?.length) return '';
        const match = customers.find(c => c.id === customerId);
        return match ? match.name : '';
    }, [customers]);

    const derived = React.useMemo(() => {
        if (!transfer) {
            return {
                pdfData: null,
                oysters: [],
                leaseNames: [],
                cropYears: [],
                fromLabel: '',
                toLabel: '',
                estuaryName: '',
                harvestAreaName: '',
                isFarmFrom: false,
                isFarmTo: false,
            };
        }

        const lines = transfer.coreTransferLines || [];
        const leaseMap = new Map();
        const cropMap = new Map();

        const oysters = lines.map((line) => {
            const lease = line?.group?.previousLocation?.line?.lease;
            if (lease) {
                const key = lease.id || `${lease.name || ''}-${lease.number || ''}`;
                if (!leaseMap.has(key)) {
                    leaseMap.set(key, lease);
                }
            }
            if (line?.crop) {
                cropMap.set(line.crop, true);
            }
            const leaseEstuary = formatEstuary(lease);
            const leaseHarvestArea = formatHarvestArea(lease);

            return {
                ...line,
                bags: line?.bags ?? 0,
                bins: line?.bins ?? 0,
                quantity: line?.quantity ?? 0,
                crop: line?.crop || '',
                lease: lease ? {
                    ...lease,
                    displayName: formatLease(lease),
                    estuary: leaseEstuary,
                    harvestArea: leaseHarvestArea,
                } : undefined,
            };
        });

        const leases = Array.from(leaseMap.values());
        const leaseNames = leases.map(formatLease).filter(Boolean);
        const cropYears = Array.from(cropMap.keys());
        const multipleLeases = leaseNames.length > 1;
        const multipleCropYear = cropYears.length > 1;
        const primaryLease = leases[0];
        const estuaryName = formatEstuary(primaryLease);
        const harvestAreaName = formatHarvestArea(primaryLease);
        const isFarmFrom = transfer?.source?.isFarm ?? false;
        const isFarmTo = transfer?.destination?.isFarm ?? false;

        const pdfData = {
            date: transfer.dateReady ? dateFormat(new Date(transfer.dateReady)) : dateFormat(new Date()),
            OTR: transfer.externalOTR || transfer.ExternalOTR || transfer.otr || transfer.OTR || '',
            PRN: transfer.prn || '',
            IVR: transfer.ivr || 0,
            comment: transfer.comment || transfer.Comment || transfer.comments || '',
            HarvestDate: transfer.harvestDate ? dateFormat(new Date(transfer.harvestDate)) : '',
            multipleLeases,
            multipleCropYear,
            Estuary: estuaryName,
            HarvestArea: harvestAreaName,
            Lease: multipleLeases ? undefined : formatLease(primaryLease),
            CropYear: multipleCropYear ? undefined : (cropYears[0] || transfer.crop || ''),
            from: formatWarehouse(transfer.source),
            to: transfer.isCustomer ? transfer.customer?.name : formatWarehouse(transfer.destination),
            isFarmFrom,
            isFarmTo,
        };

        return {
            pdfData,
            oysters,
            leaseNames,
            cropYears,
            fromLabel: pdfData.from,
            toLabel: pdfData.to,
            estuaryName,
            harvestAreaName,
            isFarmFrom,
            isFarmTo,
        };
    }, [transfer, getCustomerName]);

    const canCancelCustomerTransfer = React.useMemo(() => {
        if (!transfer?.isCustomer) return true;
        if (!transfer?.dateReady) return false;
        const readyDate = new Date(transfer.dateReady);
        const elapsed = Date.now() - readyDate.getTime();
        return elapsed < 2 * MS_PER_DAY;
    }, [transfer]);

    const cancelDisabled = !transfer || !!transfer?.dateCancelled || loadingCancel || (transfer?.isCustomer && !canCancelCustomerTransfer);

    const handleCreatePDF = async () => {
        if (!transfer || !derived.pdfData) return;
        setLoadingPdf(true);
        try {
            await createOTRPDF(derived.pdfData, derived.oysters, transfer.id);
        } finally {
            setLoadingPdf(false);
        }
    };

    const handleLabels = () => {
        if (!transfer) return;
        OTRLabels(transfer, true, true);
    };
    var transferText = process.env.REACT_APP_EXTERNAL_APP=='true' ? 'sale' : 'transfer';
    const handleCancel = async () => {
        if (!transfer || transfer.dateCancelled) return;
        if (transfer.isCustomer && !canCancelCustomerTransfer) {
            await Swal.fire({
                title: 'Cancellation window closed',
                text: `Customer ${transferText}s can only be cancelled within 2 days of the Date Ready.`,
                icon: 'info',
            });
            return;
        }
        const result = await Swal.fire({
            title: `Cancel ${transferText}?`,
            text: `This will mark the ${transferText} as cancelled and return your stock to shore.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, cancel it',
            cancelButtonText: 'No, keep it',
        });
        if (!result.value) return;
        setLoadingCancel(true);
        try {
            const response = await postApi(`/Transfers/Cancel/${transfer.id}`);
            if (!response?.ok) {
                const errorText = await response?.text();
                throw new Error(errorText || `Unable to cancel ${transferText}.`);
            }

            const responseText = await response.text();
            let updated = null;
            try {
                updated = responseText ? JSON.parse(responseText) : null;
            } catch (err) {
                throw new Error(responseText || `Unable to cancel ${transferText}.`);
            }

            if (updated) setTransfer(updated);
        } catch (err) {
            await Swal.fire({
                title: `Cancel ${transferText} failed`,
                text: `Cannot cancel ${transferText} - Stock has been used at next location`,
                icon: 'error',
            });
        } finally {
            setLoadingCancel(false);
        }
    };
    var capitalTransferText = transferText.charAt(0).toUpperCase() + transferText.slice(1);
    return (
        <Box component="section">
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
                <Button
                    variant="contained"
                    color="primary"
                    aria-label="Download OTR"
                    onClick={handleCreatePDF}
                    disabled={!transfer || loadingPdf}
                >
                    Download OTR
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    sx={{ minWidth: 120 }}
                    aria-label="Download Labels"
                    onClick={handleLabels}
                    disabled={!transfer}
                >
                    Get Labels
                </Button>
                <Button
                    variant="outlined"
                    color="error"
                    sx={{ minWidth: 140 }}
                    aria-label={`Cancel ${capitalTransferText}`}
                    onClick={handleCancel}
                    disabled={cancelDisabled}
                    title={transfer?.isCustomer && !canCancelCustomerTransfer ? `Customer ${capitalTransferText}s can only be cancelled within 2 days of the Date Ready.` : undefined}
                >
                    {transfer?.dateCancelled ? 'Cancelled' : (loadingCancel ? 'Cancelling...' : `Cancel ${capitalTransferText}`)}
                </Button>
            </Box>

            <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="h6" gutterBottom>{capitalTransferText} Details</Typography>
                <Divider sx={{ mb: 1 }} />
                <InfoRow label="OTR" value={transfer?.externalOTR || transfer?.otr} />
                <InfoRow label="PRN" value={transfer?.prn} />
                <InfoRow label="IVR" value={transfer?.ivr} />
                <InfoRow label="Harvest Date" value={transfer?.harvestDate ? dateFormat(new Date(transfer.harvestDate)) : ''} />
                <InfoRow label="Date Ready" value={transfer?.dateReady ? dateFormat(new Date(transfer.dateReady)) : ''} />
                <InfoRow label="Date Cancelled" value={transfer?.dateCancelled ? dateFormat(new Date(transfer.dateCancelled)) : ''} />
                <InfoRow label="From" value={derived.fromLabel} />
                <InfoRow label="To" value={derived.toLabel} />
                <InfoRow label="Estuary" value={derived.estuaryName} />
                <InfoRow label="Harvest Area" value={derived.harvestAreaName} />
                <InfoRow label="Lease(s)" value={derived.leaseNames.length ? derived.leaseNames.join(', ') : ''} />
                <InfoRow label="Crop Year(s)" value={derived.cropYears.length ? derived.cropYears.join(', ') : (transfer?.crop || '')} />
                <InfoRow label="Comments" value={transfer?.comment} />
            </Paper>

            <Paper sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Lines</Typography>
                <Divider sx={{ mb: 1 }} />
                <Grid container spacing={1}>
                    {derived.oysters.map((line, idx) => (
                        <Grid item xs={12} md={6} key={line?.id || idx}>
                            <LineRow line={line} />
                        </Grid>
                    ))}
                    {!derived.oysters.length ? (
                        <Grid item xs={12}><Typography variant="body2" color="text.secondary">No lines to display.</Typography></Grid>
                    ) : null}
                </Grid>
            </Paper>
        </Box>
    );
};

export default ViewTransfer;
